/**
 * Canonical operation model for the hybrid track-changes engine.
 *
 * Defined once here because every stage after the diff (char explosion T-005, position resolver
 * T-008, pre-flight T-009, execution T-010) reads it. An `Op` describes one contiguous run of the
 * diff between the tokenized baseline and target:
 *
 *  - `keep`   — text present in BOTH baseline and target, unchanged. Left untouched on apply, which
 *               is how existing counterparty revisions are preserved (they diff as `keep`).
 *  - `delete` — text present only in the baseline; to be struck through as a tracked deletion.
 *  - `insert` — text present only in the target; to be added as a tracked insertion.
 *
 * `value` is the exact substring (including trailing whitespace) so reconstruction stays lossless.
 * Ops are emitted in **baseline order** (left-to-right). Positions are NOT assigned here — that is
 * T-008, which needs the live Word context.
 */
export type OpKind = 'keep' | 'delete' | 'insert';

export interface Op {
  kind: OpKind;
  /** Exact text of this run, trailing whitespace included. */
  value: string;
}

/**
 * A pre-existing tracked revision in the live paragraph, expressed as a span over the
 * **visible baseline text** (`baselineVisibleText` — exactly what `Range.search`/`range.text`
 * returns, per the T-001 spike). `[start, end)`, half-open, in UTF-16 code units.
 *
 *  - `insertion` — text someone already added as a tracked insertion. It is part of the accepted
 *    state, so it also appears in the AI's clean target; the diff keeps it. Currently informational
 *    for the visible-target transform (the outcome follows from the clean target containing it) and
 *    carried for observability / T-007.
 *  - `deletion` — text already struck through. It is present in the visible baseline but absent
 *    from the accepted state. The visible-target transform must re-insert it so it survives as a
 *    `keep` (staying struck) rather than being re-flagged.
 */
export interface RevisionSpan {
  kind: 'insertion' | 'deletion';
  /** Inclusive start offset into `baselineVisibleText`. */
  start: number;
  /** Exclusive end offset into `baselineVisibleText`. */
  end: number;
  /**
   * Who made the revision: `theirs` = counterparty (rendered red in the preview), `ours` = one of
   * our submit authors (rendered blue). Used only by the preview renderer; the visible-target
   * transform ignores it.
   */
  side?: 'ours' | 'theirs';
}

/**
 * The revision layout of the live paragraph, in visible-baseline coordinates. Spans must be sorted
 * by `start` and non-overlapping (as Word reports revisions in document order).
 */
export interface RevisionContext {
  revisions: RevisionSpan[];
}

/**
 * A span in the live paragraph that the engine must **never edit into or split** — a cross-reference
 * field's full extent (its result text as it appears in the baseline string) or a bookmark's range.
 * Coordinates are offsets into the visible baseline string (`range.text`), `[start, end)` half-open,
 * UTF-16 code units. The T-001 spike confirmed fields render as plain result text fused to their
 * neighbours (no delimiter char in `range.text`), which is exactly why edits blindly land inside
 * them; this span map lets the resolver keep ops out.
 */
export interface ProtectedRegion {
  kind: 'field' | 'bookmark';
  /** Inclusive start offset into the visible baseline string. */
  start: number;
  /** Exclusive end offset into the visible baseline string. */
  end: number;
}

/** Count of ops by kind — a text-free histogram for observability (never logs clause content). */
export interface OpHistogram {
  keep: number;
  delete: number;
  insert: number;
}

/** Tally ops by kind. Pure; emits counts only, never text. */
export function opHistogram(ops: Op[]): OpHistogram {
  const h: OpHistogram = { keep: 0, delete: 0, insert: 0 };
  for (const op of ops) h[op.kind]++;
  return h;
}
